import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { GuestAuthPrompt } from "@/components/GuestAuthPrompt";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import { DURATION, EASE_OUT_EXPO, usePrefersReducedMotion } from "@/lib/motion";
import { formatLocaleDate } from "@/lib/dates";
import type { BookingLocale } from "@/i18n";
import type { PriceQuote } from "@/types";

type Props = {
  siteName: string;
  logoUrl: string | null;
  title: string;
  message: string;
  bookingNumber: string;
  bookingNumberLabel: string;
  showBookingNumber?: boolean;
  arrival: string;
  departure: string;
  arrivalLabel: string;
  departureLabel: string;
  locale: BookingLocale;
  quote: PriceQuote;
  touristIntegrationEnabled: boolean;
  guestLoggedIn: boolean;
  portalLabel: string;
  portalPath: string;
};

export function BookingSuccessScreen({
  siteName,
  logoUrl,
  title,
  message,
  bookingNumber,
  bookingNumberLabel,
  showBookingNumber = true,
  arrival,
  departure,
  arrivalLabel,
  departureLabel,
  locale,
  quote,
  touristIntegrationEnabled,
  guestLoggedIn,
  portalLabel,
  portalPath,
}: Props) {
  const reducedMotion = usePrefersReducedMotion();

  const cardVariants = reducedMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 24, scale: 0.96 },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: DURATION.slow, ease: EASE_OUT_EXPO },
        },
      };

  const numberVariants = reducedMotion
    ? undefined
    : {
        hidden: { opacity: 0, scale: 0.85 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: { duration: DURATION.slow, ease: EASE_OUT_EXPO, delay: 0.2 },
        },
      };

  const checkVariants = reducedMotion
    ? undefined
    : {
        hidden: { opacity: 0, scale: 0 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: { type: "spring", stiffness: 300, damping: 20, delay: 0.1 },
        },
      };

  return (
    <div className="bk-page min-h-screen py-10">
      <BookingBrandHeader siteName={siteName} logoUrl={logoUrl} title={title} className="mb-8" />
      <motion.div
        className="bk-card space-y-4 text-center"
        initial={reducedMotion ? false : "hidden"}
        animate="visible"
        variants={cardVariants}
      >
        <motion.div
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"
          variants={checkVariants}
        >
          <svg className="h-8 w-8 text-emerald-600" viewBox="0 0 24 24" fill="none" aria-hidden>
            <motion.path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reducedMotion ? false : { pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.25 }}
            />
          </svg>
        </motion.div>

        <p className="text-slate-700">{message}</p>
        {showBookingNumber ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {bookingNumberLabel}
            </p>
            <motion.p
              className="text-3xl font-bold text-brand-700"
              variants={numberVariants}
            >
              {bookingNumber}
            </motion.p>
          </>
        ) : null}
        <dl className="space-y-2 text-left text-sm text-slate-600">
          <div className="flex justify-between">
            <dt>{arrivalLabel}</dt>
            <dd>{formatLocaleDate(arrival, locale)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>{departureLabel}</dt>
            <dd>{formatLocaleDate(departure, locale)}</dd>
          </div>
        </dl>
        <PriceBreakdown quote={quote} className="border-brand-100" />
        {!touristIntegrationEnabled && !guestLoggedIn ? (
          <GuestAuthPrompt
            guestLoggedIn={false}
            variant="inline"
            context="confirmation"
            returnTo={portalPath}
            className="mt-4 text-left"
          />
        ) : !touristIntegrationEnabled && guestLoggedIn ? (
          <Link to={portalPath} className="bk-btn-secondary mt-4 inline-flex w-full justify-center">
            {portalLabel}
          </Link>
        ) : null}
      </motion.div>
    </div>
  );
}
